import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, PhoneOutgoing, Send, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { dialNumber } from "@/lib/phone";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const PAGE_SIZE = 50;

export default function Renewals() {
  const [kind, setKind] = useState("due");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sel, setSel] = useState({});
  const [agents, setAgents] = useState([]);
  const [agent, setAgent] = useState("");
  const navigate = useNavigate();

  const load = () => api.get(`/renewals?kind=${kind}&page=${page}&page_size=${PAGE_SIZE}${fromDate ? `&from_date=${fromDate}` : ""}${toDate ? `&to_date=${toDate}` : ""}`)
    .then((r) => { setRows(r.data.items); setTotal(r.data.total); setSel({}); })
    .catch(() => {});
  useEffect(() => { setPage(1); }, [kind, fromDate, toDate]);
  useEffect(() => { load(); }, [kind, page, fromDate, toDate]);
  useEffect(() => { api.get("/users").then((r) => setAgents(r.data.filter((u) => u.active))).catch(() => {}); }, []);

  const clearDates = () => { setFromDate(""); setToDate(""); };

  const toggle = (id) => setSel((s) => ({ ...s, [id]: !s[id] }));
  const selected = Object.keys(sel).filter((k) => sel[k]);
  const allChecked = rows.length > 0 && selected.length === rows.length;
  const toggleAll = () => setSel(allChecked ? {} : Object.fromEntries(rows.map((r) => [r.id, true])));

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  const assign = async () => {
    if (!agent) { toast.error("Choose an agent"); return; }
    if (selected.length === 0) { toast.error("Select customers"); return; }
    try {
      await api.post("/call-tasks", { customer_ids: selected, assigned_to: agent, reason: kind === "expired" ? "Expired" : "Renewal" });
      toast.success(`Sent ${selected.length} to ${agent} for outbound calls`);
      setSel({});
    } catch (e) { toast.error("Could not assign — try again"); }
  };

  return (
    <div className="space-y-6">
      <div><h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-2"><RefreshCw size={24} className="text-primary" /> Renewals</h1><p className="text-muted-foreground mt-1">Expired or due-within-90-days subscriptions — assign to an agent for outbound calls.</p></div>

      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="flex items-end gap-3 flex-wrap">
          <Tabs value={kind} onValueChange={setKind}>
            <TabsList className="rounded-sm"><TabsTrigger value="due" className="rounded-sm" data-testid="filter-due">Due soon</TabsTrigger><TabsTrigger value="expired" className="rounded-sm" data-testid="filter-expired">Expired</TabsTrigger></TabsList>
          </Tabs>
          <div className="grid gap-1">
            <Label className="text-xs text-muted-foreground">Ends from</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="rounded-sm w-40" data-testid="filter-from-date" />
          </div>
          <div className="grid gap-1">
            <Label className="text-xs text-muted-foreground">Ends to</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="rounded-sm w-40" data-testid="filter-to-date" />
          </div>
          {(fromDate || toDate) && <Button variant="ghost" size="sm" className="rounded-sm text-muted-foreground" onClick={clearDates} data-testid="clear-dates">Clear</Button>}
        </div>
        <div className="flex items-center gap-2">
          <Select value={agent} onValueChange={setAgent}>
            <SelectTrigger className="rounded-sm w-48" data-testid="assign-agent"><SelectValue placeholder="Assign to agent" /></SelectTrigger>
            <SelectContent>{agents.map((a) => <SelectItem key={a.id} value={a.name}>{a.name} ({a.role})</SelectItem>)}</SelectContent>
          </Select>
          <Button className="rounded-sm gap-2" onClick={assign} data-testid="assign-btn"><Send size={15} /> Send ({selected.length})</Button>
        </div>
      </div>

      <div className="rounded-sm border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead className="w-10"><Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="select-all" /></TableHead><TableHead>Customer</TableHead><TableHead>Phone</TableHead><TableHead>Plan</TableHead><TableHead>Ends</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody data-testid="renewals-body">
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Nothing here right now.</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate(`/clients/${r.id}`)} data-testid={`renewal-row-${r.id}`}>
                <TableCell onClick={(e) => e.stopPropagation()}><Checkbox checked={!!sel[r.id]} onCheckedChange={() => toggle(r.id)} data-testid={`sel-${r.id}`} /></TableCell>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="font-mono text-sm">{r.phone || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{r.plan || "—"}</TableCell>
                <TableCell><Badge variant="outline" className={`rounded-sm ${kind === "expired" ? "bg-primary/15 text-primary border-primary/30" : "bg-warning/15 text-warning border-warning/30"}`}>{r.end}</Badge></TableCell>
                <TableCell className="text-right"><button onClick={(e) => { e.stopPropagation(); dialNumber(r.phone); }} className="text-primary hover:opacity-80" title="Call" data-testid={`dial-${r.id}`}><PhoneOutgoing size={16} /></button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground" data-testid="renewals-page-info">
          {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-sm gap-1" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="renewals-prev-page"><ChevronLeft size={15} /> Prev</Button>
          <span className="text-sm px-2" data-testid="renewals-page-count">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" className="rounded-sm gap-1" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="renewals-next-page">Next <ChevronRight size={15} /></Button>
        </div>
      </div>
    </div>
  );
}
